import dayjs from 'dayjs'

import { type PatientAppointmentType } from 'features/Schedule/pusher/type'

const ECHO_TTL_MS = 15_000
// A `pending` snapshot only suppresses events while the local mutation is in flight. Keep its TTL
// short so a stuck/failed mutation can't silently hide real Pusher events for the full 15s.
const PENDING_ECHO_TTL_MS = 4_000

type NormalizedPatient = {
  id: number
  date: string
  updated_at: string
  emr_updated_at: string | null
  roomCode: string
  start_time: string
  end_time: string
  status_code: number | undefined
  temperature: number | undefined
  consultation_staff: number | undefined
  sms_notification: string | number | undefined
  patient_id: number | undefined
}

type AppointmentEchoSnapshot =
  | { kind: 'pending'; expiresAt: number }
  | { kind: 'upsert'; expiresAt: number; normalized: NormalizedPatient }
  | { kind: 'delete'; expiresAt: number }
  | { kind: 'status'; expiresAt: number; status_code: number; temperature: number }

type RoomEchoSnapshot = { expiresAt: number; roomStatusCode: number }

const appointmentEchoes = new Map<number, AppointmentEchoSnapshot>()
const roomEchoes = new Map<number, RoomEchoSnapshot>()

const normalizeTimestamp = (value?: string | null): string => {
  if (!value) return ''
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.toISOString() : value
}

const normalizeTime = (value?: string | Date | null): string => {
  if (!value) return ''
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('HH:mm:ss') : String(value)
}

const normalizePatient = (data: PatientAppointmentType): NormalizedPatient => {
  const { room, patient, start_time, end_time, status, emr_updated_at, ...rest } = data

  return {
    id: rest.id,
    date: rest.date,
    updated_at: normalizeTimestamp(rest.updated_at),
    emr_updated_at: emr_updated_at ?? null,
    roomCode: room?.code ?? '',
    start_time: normalizeTime(start_time),
    end_time: normalizeTime(end_time),
    status_code: status?.code,
    temperature: rest.temperature,
    consultation_staff: rest.consultation_staff,
    sms_notification: rest.sms_notification,
    patient_id: patient?.id,
  }
}

const isExpired = (expiresAt: number) => Date.now() > expiresAt

const getAppointmentEcho = (appointmentId: number): AppointmentEchoSnapshot | undefined => {
  const snapshot = appointmentEchoes.get(appointmentId)
  if (!snapshot) return undefined
  if (isExpired(snapshot.expiresAt)) {
    appointmentEchoes.delete(appointmentId)
    return undefined
  }
  return snapshot
}

const hasMeaningfulPatientChange = (
  incoming: NormalizedPatient,
  stored: NormalizedPatient,
): boolean => {
  if (incoming.updated_at !== stored.updated_at) return true

  return (
    incoming.date !== stored.date ||
    incoming.roomCode !== stored.roomCode ||
    incoming.start_time !== stored.start_time ||
    incoming.end_time !== stored.end_time ||
    incoming.status_code !== stored.status_code ||
    incoming.temperature !== stored.temperature ||
    incoming.consultation_staff !== stored.consultation_staff ||
    incoming.sms_notification !== stored.sms_notification ||
    incoming.patient_id !== stored.patient_id ||
    incoming.emr_updated_at !== stored.emr_updated_at
  )
}

export const markScheduleMutationPending = (appointmentId: number) => {
  appointmentEchoes.set(appointmentId, {
    kind: 'pending',
    expiresAt: Date.now() + PENDING_ECHO_TTL_MS,
  })
}

export const clearScheduleMutationEcho = (appointmentId: number) => {
  appointmentEchoes.delete(appointmentId)
}

export const recordScheduleMutationUpsert = (data: PatientAppointmentType) => {
  appointmentEchoes.set(data.id, {
    kind: 'upsert',
    expiresAt: Date.now() + ECHO_TTL_MS,
    normalized: normalizePatient(data),
  })
}

export const recordScheduleMutationDelete = (appointmentId: number) => {
  appointmentEchoes.set(appointmentId, {
    kind: 'delete',
    expiresAt: Date.now() + ECHO_TTL_MS,
  })
}

export const recordScheduleMutationStatus = (
  appointmentId: number,
  status_code: number,
  temperature: number,
) => {
  appointmentEchoes.set(appointmentId, {
    kind: 'status',
    expiresAt: Date.now() + ECHO_TTL_MS,
    status_code,
    temperature,
  })
}

export const recordScheduleMutationRoomStatus = (roomId: number, roomStatusCode: number) => {
  roomEchoes.set(roomId, {
    expiresAt: Date.now() + ECHO_TTL_MS,
    roomStatusCode,
  })
}

export const isRedundantPusherPatientUpsert = (data: PatientAppointmentType): boolean => {
  const snapshot = getAppointmentEcho(data.id)
  if (!snapshot) return false
  if (snapshot.kind === 'pending' || snapshot.kind === 'delete') return true
  if (snapshot.kind !== 'upsert') return false

  const incoming = normalizePatient(data)
  const stored = snapshot.normalized

  return !hasMeaningfulPatientChange(incoming, stored)
}

export const isRedundantPusherPatientDelete = (appointmentId: number): boolean => {
  const snapshot = getAppointmentEcho(appointmentId)
  return snapshot?.kind === 'delete' || snapshot?.kind === 'pending'
}

export const isRedundantPusherPatientStatus = (
  appointmentId: number,
  status_code: number,
  temperature: number,
): boolean => {
  const snapshot = getAppointmentEcho(appointmentId)
  if (!snapshot) return false
  if (snapshot.kind === 'pending') return true

  if (snapshot.kind === 'status') {
    return snapshot.status_code === status_code && snapshot.temperature === temperature
  }

  if (snapshot.kind === 'upsert') {
    return (
      snapshot.normalized.status_code === status_code &&
      snapshot.normalized.temperature === temperature
    )
  }

  return false
}

export const isRedundantPusherRoomStatus = (roomId: number, roomStatusCode: number): boolean => {
  const snapshot = roomEchoes.get(roomId)
  if (!snapshot) return false
  if (isExpired(snapshot.expiresAt)) {
    roomEchoes.delete(roomId)
    return false
  }
  return snapshot.roomStatusCode === roomStatusCode
}
